<script lang="ts">
    import { get_current_component, onMount } from 'svelte/internal'
    import Fa from 'svelte-fa'
    import { faAngleLeft, faEdit, faPlusCircle, faSave, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
    import dayjs from 'dayjs'
    // ##############################################################################################################
    import { DocumentsRepository } from '../../../modules/toolsApi/documents/documents.repository'
    import { DocumentsService } from '../../../modules/toolsApi/documents/documents.service'
    import { Document } from '../../../modules/toolsApi/documents/document'
    import type { CreateDocumentDto } from '../../../modules/toolsApi/documents/dto/create-document.dto'
    import type { UpdateDocumentDto } from '../../../modules/toolsApi/documents/dto/update-document.dto'
    import Content from '../../_common/content.svelte'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import { quill } from 'svelte-quill'

    const entityId = 'documentId'
    const rep = new DocumentsRepository()
    const service = new DocumentsService(rep)
    let show = false
    let step = 1
    let entities = []
    let entity: Document = new Document()

    var toolbarOptions = [
        //[{ font: [] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'], // toggled buttons
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }], // outdent/indent

        [{ color: [] }, { background: [] }], // dropdown with defaults from theme
        [{ align: [] }],
        ['image'],
        ['clean'], // remove formatting button
    ]
    let options = { placeholder: '', modules: { toolbar: toolbarOptions } }
    let content = { html: '', text: '' }

    async function editEntity(id: string) {
        try {
            show = true
            entity = new Document()
            entity = await service.getOne(id)
            step = 2
            setTimeout(() => {
                document.querySelector('.ql-editor').innerHTML = entity.content
            }, 100)
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function newEntity() {
        entity = new Document()
        step = 2
    }

    async function createEntity() {
        try {
            show = true
            const dto: CreateDocumentDto = {
                title: entity.title,
                content: content.html,
            }
            entity = await service.create(dto)
            window.api.users.statistics.tick('document.generate')
            await loadEntities()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function updateEntity() {
        try {
            show = true
            const dto: UpdateDocumentDto = {
                title: entity.title,
                content: content.html,
            }
            await service.update(entity[entityId], dto)
            await loadEntities()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function saveEntity() {
        if (entity[entityId]) {
            updateEntity()
        } else {
            createEntity()
        }
    }

    async function loadEntities() {
        try {
            show = true
            step = 1
            entities = await service.getAll()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function removeEntity() {
        if (!confirm('Deseja mesmo excluir este registro?')) {
            return
        }
        try {
            show = true
            await service.remove(entity[entityId])
            await loadEntities()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function evaluateError(error) {
        window.error(error)
    }

    function showEntities() {
        step = 1
    }

    onMount(async () => {
        await loadEntities()
    })
</script>

<svelte:head>
    <link href="//cdn.quilljs.com/1.3.6/quill.snow.css" rel="stylesheet" />
</svelte:head>

{#if step === 1}
    <Content>
        <div class="grid-act">
            <div class="spacer" />
            {#if window.store.token.permissions.tools.docs.write}
                <button class="btn btn-primary" on:click={newEntity}><Fa icon={faPlusCircle} /> Novo documento</button>
            {/if}
        </div>
        <div class="grid-head">
            <div class="row">
                <div class="col">Título do documento</div>
                <div class="col-md-1" />
            </div>
        </div>
        <div class="grid-body">
            {#if !show}
                {#each entities as item}
                    <div class="row line liner py-1">
                        <div class="col">{item.title}</div>
                        <div class="col-md-1 text-end">
                            <button
                                class="btn btn-primary btn-sm"
                                on:click={() => {
                                    editEntity(item.documentId)
                                }}
                            >
                                <Fa icon={faEdit} /> Editar
                            </button>
                        </div>
                    </div>
                {/each}
            {/if}
        </div>
        <LoadSpinner {show} />
    </Content>
{/if}
{#if step === 2}
    <Content>
        <div class="grid-act">
            <button class="btn btn-secondary" on:click={showEntities}><Fa icon={faAngleLeft} /> Voltar</button>
            <div class="spacer" />
            {#if window.store.token.permissions.tools.docs.write}
                {#if !!entity[entityId]}
                    <button class="btn btn-warning" on:click={removeEntity}><Fa icon={faTrashAlt} /> Excluir</button>
                {/if}
                <button class="btn btn-primary" on:click={saveEntity}><Fa icon={faSave} /> Salvar</button>
            {/if}
        </div>
        <div class="grid-form m-bottom-sm">
            <div class="row">
                <div class="col">
                    <div class="input-group">
                        <label for="" class="input-group-text">Título do documento</label>
                        <input type="text" bind:value={entity.title} class="form-control" />
                    </div>
                </div>
            </div>
            <div class="row mb-3">
                <div class="col">
                    <div class="editor" use:quill={options} on:text-change={(e) => (content = e.detail)} />
                </div>
                <div class="text-muted mt-1">
                    Chaves de personalização disponíveis:
                    <span title="Nome completo da pessoa" class="code">&#123;nome&#125;</span>
                    <span title="Primeiro nome da pessoa" class="code">&#123;primeironome&#125;</span>
                    <span title="Celular da pessoa" class="code">&#123;celular&#125;</span>
                    <span title="E-Mail da pessoa" class="code">&#123;email&#125;</span>
                    <span title="CPF / CNPJ da pessoa" class="code">&#123;documento&#125;</span>
                    <span title="Endereço da pessoa" class="code">&#123;endereco&#125;</span>
                    <span title="Data atual" class="code">&#123;data&#125;</span>
                    <span title="Hora atual" class="code">&#123;hora&#125;</span>
                    <span title="Detalhamento da compra" class="code">&#123;compra&#125;</span>
                    <span title="Data da compra" class="code">&#123;datacompra&#125;</span>
                    <span title="Detalhamento do pagamento" class="code">&#123;pagamento&#125;</span>
                    <span title="Quebra de página" class="code">&#123;novapagina&#125;</span>
                </div>
            </div>
        </div>
    </Content>
{/if}

<style>
    :global(.ql-toolbar) {
        background-color: white;
    }
    .editor {
        background-color: #ccc;
        color: black;
        height: calc(100vh - 360px);
        font-size: 18px;
        overflow: auto !important;
        min-height: 450px;
    }
    :global(.ql-editor) {
        max-width: 1024px;
        margin: 16px auto;
        background-color: white;
        overflow: unset !important;
        height: unset !important;
        min-height: 1460px;
    }
    :global(.ql-editor ol, .ql-editor ul) {
        padding-left: 0px !important;
    }
</style>
